import React, {
    useContext,
    useEffect,
    useState
} from "react";
import {
    ButtonWrapper,
    CardForm,
    CardFormHeading,
    Column,
    ColumnWrapper,
    Disclaimer,
    InfoWrapper
} from "./styled";
import {
    DateInput,
    EmailInput,
    FileInput,
    NumberInput,
    SelectAsset,
    SelectCategory,
    SelectLocation,
    SelectTeam,
    SelectUser,
    TextAreaInput,
    TextInput,
    WarningWrapper
} from "components";
import MCQ from "./FormItemInputs/MCQ";
import Task from "./FormItemInputs/Task";
import Checklist from "./FormItemInputs/Checklist";
import { useAlert } from 'react-alert';

import Layout from "views/Layout";
import { AppContext, withAppContext } from "hocs/withAppContext";
import { ReactComponent as AttachPDFIcon } from "assets/svgs/attach-pdf.svg";
import { ReactComponent as AttachImageIcon } from "assets/svgs/attach-image.svg";
import { createWORequest } from "../service";
import { withEmail } from "../../../hocs/withEmail";

const FormItemComponentMap = {
    'Multiple Choice Form': ({ formItem, ...props }) => <MCQ
        values={formItem.multipleChoiceAnswers}
        {...props}
    />,
    'Text Form Item': ({ formItem, ...props }) => <TextInput
        placeholder="Enter value"
        {...props}
    />,
    'Task': ({ formItem, ...props }) => <Task {...props}/>,
    'Number Form Item': ({ formItem, ...props }) => <NumberInput
        placeholder="Enter value"
        {...props}
    />,
    'Checklist Form Item': ({ formItem, ...props }) => <Checklist {...props}/>,
};

const CreateWorkOrderRequest = withEmail(({ email }) => {
    const defaultRequest = {
        name: '',
        phoneNumber: '',
        mainDescription: '',
        note: '',
        requesterEmailAddress: email,
        arrayOfFiles: []
    };
    const alert = useAlert();
    const { role, helpers: { n } } = useContext(AppContext);
    const [errors, setErrors] = useState({});
    const [request, setRequestData] = useState(defaultRequest);

    const defaultFormItems = (role['arrayOfPublicRequestFormItems'] || []);
    const [formItems, setFormItems] = useState(defaultFormItems);

    const set = (name, value) => setRequestData({ ...request, [name]: value });
    // list of keys that should be present in the
    // validated request data
    const requiredFieldsMap = {
        'mainDescription': true,
        'note': true,
        'requesterEmailAddress': true,
        'objectLocationForWorkOrder': role['areLocationsVisiblePublicallyIsRequired'] === true,
        'objectAsset': role['areAssetsVisiblePublicallyIsRequired'] === true,
        'categoryType': role['areCategoriesVisiblePublicallyIsRequired'] === true,
        'userAssignedTo': role['areUsersVisiblePublicallyIsRequired'] === true,
        'objectProject': role['areTeamsVisiblePublicallyIsRequired'] === true,
        'name': role['isNameRequiredForPublicRequest'] === true,
        'phoneNumber': role['isPhoneNumberRequiredForPublicRequest'] === true,
        'dueDate': role['isDueDateVisiblePublicallyIsRequired'] === true,
        
    };

    // components that are not always visible by default
    // the admins have the option to make them visible
    // and even mark them as required
    // these are usually driven by organizations'
    // internal policy which may vary
    const componentMap = {
        'Locations': ({ n, key, ...props }) =>
            <div key={key}>
                <SelectLocation
                    {...props}
                    label={n('location')}
                    placeholder={`Select ${n('location')}`}
                    value={request['objectLocationForWorkOrder']}
                    onChange={val => set('objectLocationForWorkOrder', val)}
                    required={requiredFieldsMap['objectLocationForWorkOrder']}
                    error={errors['objectLocationForWorkOrder']}
                    disabled={Boolean(request['objectAsset'])}
                />
                {request['objectAsset'] && <WarningWrapper>
                    Trying to assign a location to your work order?
                    If you need to update the location, please remove the asset from
                    your work order and select the desired location!
                </WarningWrapper>}
            </div>,
        'Assets': ({ n, ...props }) =>
            <SelectAsset
                {...props}
                label={n('asset')}
                placeholder={`Select ${n('asset')}`}
                value={request['objectAsset']}
                onChange={val => set('objectAsset', val)}
                required={requiredFieldsMap['objectAsset']}
                error={errors['objectAsset']}
                location={request['objectLocationForWorkOrder']}
            />,
        'Categories': (props) =>
            <SelectCategory
                {...props}
                label="Category"
                placeholder="Select Category"
                value={request['categoryType']}
                onChange={val => set('categoryType', val)}
                required={requiredFieldsMap['categoryType']}
                error={errors['categoryType']}
            />,
        'Users': (props) =>
            <SelectUser
                {...props}
                label="User"
                placeholder="Select User"
                value={request['userAssignedTo']}
                onChange={val => set('userAssignedTo', val)}
                required={requiredFieldsMap['userAssignedTo']}
                error={errors['userAssignedTo']}
            />,
        'DueDate': (props) =>
            <DateInput
                {...props}
                label="Due Date"
                date={ '' }
                placeholder="Enter Due Date"
                required={requiredFieldsMap['dueDate']}
                onChange={val => set('dueDate', val)}
                error={errors['dueDate']}
            />,
        'Teams': (props) =>
            <SelectTeam
                {...props}
                label="Team"
                placeholder="Select Team"
                value={request['objectProject']}
                onChange={val => set('objectProject', val)}
                required={requiredFieldsMap['objectProject']}
                error={errors['objectProject']}
            />
    };

    // some components are dynamically rendered as
    // different customers have different requirements
    // these can be configured by admins on the web app
    // there are 2 flags
    // - one determines whether the field needs to shown
    // - other determines whether the field is required or not!
    // these flags help our customers maintain data integrity
    // acc to their internal processes
    const components = Object.keys(componentMap)
        .filter(field => {
            if(`is${field}VisiblePublically` === 'isDueDateVisiblePublically') {
                return role[`is${field}VisiblePublically`] === true
            } else {
                return role[`are${field}VisiblePublically`] === true
            }
        })
        .map(field => componentMap[field]({
            n,
            key: field.toLowerCase(),
        }));

    const additionalFieldComponents = formItems
        .map((formItem, index) => FormItemComponentMap[formItem.formType]({
            formItem,
            key: index,
            label: formItem.formName,
            required: formItem.requiredForPublicRequest === true,
            error: errors[formItem.formName],
            value: formItem._value,
            onChange: (val) => {
                formItems.splice(index, 1, { ...formItem, _value: val });
                setFormItems([...formItems]);
            }
        }));

    const validate = () => {
        Object.keys(requiredFieldsMap)
            .map(field => requiredFieldsMap[field] && !request[field]
                ? errors[field] = 'This field is required!'
                : delete errors[field]);
        formItems
            .filter(formItem => formItem.requiredForPublicRequest === true)
            .forEach(formItem => {
                if (formItem._value) {
                    delete errors[formItem.formName];
                } else {
                    errors[formItem.formName] = 'This field is required!'
                }
            });
        setErrors({ ...errors });
        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        // re-evaluate errors only after the form
        // has been marked dirty, just a better UX
        // this effect can be removed safely
        // it hides the errors as soon as they are fixed
        // it should not run unless user has tried
        // submitting the form at least once
        if (Object.keys(errors).length !== 0) {
            validate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, formItems]);

    useEffect(() => {
        // scroll to fault input once the errors have been identified,
        // take one error at the time
        const faultyInput = document.querySelector('[data-error="true"]')
            || document.querySelector('.data-error');
        if (!faultyInput) {
            return;
        }

        const focusedElement = document.activeElement.tagName;
        if (['INPUT', 'TEXTAREA'].indexOf(focusedElement) !== -1) {
            // we want to make sure to not steal focus
            // from any existing element and deteriorate the UX
            return;
        }

        faultyInput.scrollIntoView();
        faultyInput.focus();
    }, [errors]);

    const [loading, setLoading] = useState(false);

    // use the magic of react,
    // some of our components are not controlled
    // like custom form items,
    // and file uploads
    const [key, setKey] = useState(Math.random().toString(36).substring(2));
    const changeKey = () => setKey(Math.random().toString(36).substring(2));

    const handleOnSubmit = () => {
        if (validate() && !loading) {
            setLoading(true);
            // Update description with requester's details
            createWORequest(role.objectId, request, formItems)
                .then(() => {
                        setRequestData({
                            ...defaultRequest,
                            name: request.name,
                            phoneNumber: request.phoneNumber
                        });

                        formItems.forEach(formItem => delete formItem._value);
                        setFormItems([...formItems]);
                    }
                )
                .then(() => {
                    setLoading(false);
                    alert.success('Your request has been submitted!');
                    changeKey();
                })
                .catch((err) => {
                    setLoading(false);
                    alert.error(err);
                });
        }
    };

    return <Layout key={key}>
        <ColumnWrapper>
            <Column>
                <CardFormHeading>Requester Info</CardFormHeading>
                <CardForm>
                    <TextInput
                        label="Full Name"
                        placeholder="Your Full Name"
                        required={requiredFieldsMap['name']}
                        value={request['name']}
                        onChange={val => set('name', val)}
                        error={errors['name']}
                    />

                    <TextInput
                        label="Phone Number"
                        placeholder="Your Phone Number"
                        type="phone"
                        required={requiredFieldsMap['phoneNumber']}
                        value={request['phoneNumber']}
                        onChange={val => set('phoneNumber', val)}
                        error={errors['phoneNumber']}
                    />

                    <EmailInput
                        label="Email Address"
                        placeholder="Your Email Address"
                        value={request['requesterEmailAddress']}
                        onChange={val => set('requesterEmailAddress', val)}
                        required
                        error={errors['requesterEmailAddress']}
                    />

                    <InfoWrapper>
                        You can keep track of your requests by logging in to the Portal with the same email address.
                    </InfoWrapper>
                </CardForm>
            </Column>
            <Column>
                <CardFormHeading>Request Details</CardFormHeading>
                <CardForm>
                    <TextInput
                        label="Title"
                        placeholder="Title to describe the issue"
                        required
                        value={request['mainDescription']}
                        onChange={val => set('mainDescription', val)}
                        error={errors['mainDescription']}
                    />

                    <TextAreaInput
                        label="Description"
                        placeholder="Provide some details for this maintenance request"
                        rows="6"
                        required
                        value={request['note']}
                        onChange={val => set('note', val)}
                        error={errors['note']}
                    />

                    {components}
                </CardForm>
            </Column>

            <Column/>
            <Column>
                <CardFormHeading>Files</CardFormHeading>
                <CardForm>
                    <FileInput
                        placeholder="Click to upload an Image"
                        onChange={val => set('completedImageFile', val[0])}
                        error={errors['completedImageFile']}
                    >
                        <AttachImageIcon/>
                    </FileInput>
                    <FileInput
                        accept="application/pdf"
                        placeholder="Click to upload a File"
                        onChange={val => set('arrayOfFiles', val)}
                        error={errors['arrayOfFiles']}
                    >
                        <AttachPDFIcon/>
                    </FileInput>
                </CardForm>
            </Column>

            <Column/>
            {formItems.length !== 0 && <Column>
                <CardFormHeading>Additional Info</CardFormHeading>
                <CardForm>
                    {additionalFieldComponents}
                </CardForm>
            </Column>}
        </ColumnWrapper>

        <ColumnWrapper>
            <Column>
                <ButtonWrapper onClick={handleOnSubmit} data-loading={loading}>Submit Work Order Request</ButtonWrapper>
                <Disclaimer>All requests are sent directly to the company admin.</Disclaimer>
            </Column>
        </ColumnWrapper>
    </Layout>
});


export default withAppContext(CreateWorkOrderRequest);
